import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { activeCurrency } from '../datatypes/currency';
import { IShopAccountRegister, ShopVisibility } from '../api/model/requests';
import { getOrCreateDeviceId } from '../datatypes/device';
import { UserProfile } from '../datatypes/user-profile';
import { write } from '../language/lang';
import { styles as sharedStyles } from '../styles/shared-styles';

// Compose both consumers — we need the user (for owner-role check) AND the
// active shop (the thing whose settings we're editing).
const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

@customElement('shop-settings-page')
export class ShopSettingsPage extends Base {
  @state() private _shop: IShopAccountRegister | null = null;
  @state() private _displayName = '';
  @state() private _timezone = '';
  @state() private _visibility: ShopVisibility = 'private';
  @state() private _waitWarn = 10;
  @state() private _waitDanger = 20;
  // Phase K thresholds. Stored as fractions server-side (0.20); shown as
  // percentages in the UI for operator clarity.
  @state() private _variancePct = 20;
  @state() private _lossAmount = 500;
  @state() private _busy = false;
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _saved = false;

  // Device-binding section state. Loaded after the form initialises so
  // the page renders without waiting on an extra round-trip.
  @state() private _deviceId: string | null = null;
  @state() private _bindingKind: 'personal' | 'shop-owned' | 'unknown' =
    'unknown';
  @state() private _bindingShopName: string | null = null;
  @state() private _bindingBusy = false;
  @state() private _bindingMessage: {
    kind: 'success' | 'danger';
    text: string;
  } | null = null;

  // Sample-shop reset (owner self-serve). Only visible when the
  // active shop has IsSample=true on the server's register row.
  @state() private _resetDialogOpen = false;
  @state() private _resetConfirmText = '';
  @state() private _resetBusy = false;
  @state() private _resetMessage: {
    kind: 'success' | 'danger';
    text: string;
  } | null = null;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 560px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .visibility-row {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border: 1px solid var(--sl-color-neutral-200);
        border-radius: var(--sl-border-radius-medium);
      }
      .vis-text {
        flex: 1;
      }
      .vis-text small {
        display: block;
        opacity: 0.75;
      }
    `,
  ];

  firstUpdated() {
    void this._initAsync();
  }

  private async _initAsync() {
    if (!this.activeShop) {
      this._error = 'No active shop. Pick one first.';
      this._loading = false;
      return;
    }

    if (!this.userProfile?.hasRole(this.activeShop.shopName, 'owner')) {
      this._error = 'Only the shop owner can change settings.';
      this._loading = false;
      return;
    }

    const api = ApiFactory.authenticated();
    const r = await api.getShop(this.activeShop.shopName);
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Failed to load shop.';
      this._loading = false;
      return;
    }
    this._shop = r.data;
    this._displayName = r.data.displayName;
    this._timezone = r.data.timezone;
    this._visibility = r.data.visibility;
    this._waitWarn = r.data.orderWaitWarnMinutes ?? 10;
    this._waitDanger = r.data.orderWaitDangerMinutes ?? 20;
    this._variancePct = Math.round((r.data.varianceThresholdPct ?? 0.2) * 100);
    this._lossAmount = r.data.lossThresholdAmount ?? 500;
    this._loading = false;

    // Kick off the binding-status load. Failure here doesn't block
    // the form — the section just shows "could not load" and the
    // owner can retry by reloading.
    void this._loadBindingStatus();
  }

  private async _loadBindingStatus() {
    try {
      this._deviceId = await getOrCreateDeviceId();
      const r = await ApiFactory.authenticated().getDeviceBinding(
        this._deviceId
      );
      if (r.success && r.data) {
        this._bindingKind = r.data.kind;
        this._bindingShopName = r.data.shopName ?? null;
      } else {
        this._bindingKind = 'unknown';
      }
    } catch {
      this._bindingKind = 'unknown';
    }
  }

  private async _bindToThisShop() {
    if (!this.activeShop || !this._deviceId || this._bindingBusy) return;
    this._bindingBusy = true;
    this._bindingMessage = null;
    try {
      const api = ApiFactory.authenticated();
      // Bind as all-in-one. This is the correct value, not a placeholder: the
      // free tier (the only tier live pre-Phase-O) permits all-in-one only
      // (Subscription.Features.AllowedModes), so there is nothing to pick. The
      // per-device mode picker belongs to Phase O, where higher tiers unlock the
      // other modes (pos / kitchen / manager / …) that make a choice meaningful;
      // it edits allowedModes/defaultMode via this same upsert.
      const r = await api.upsertDevice(
        this.activeShop.shopName,
        this._deviceId,
        {
          displayName: 'This device',
          allowedModes: ['all-in-one'],
          defaultMode: 'all-in-one',
          isExclusiveToShop: true,
        }
      );
      if (!r.success) {
        this._bindingMessage = {
          kind: 'danger',
          text: r.error ?? 'Could not bind this device.',
        };
        return;
      }
      this._bindingMessage = {
        kind: 'success',
        text: 'This device is now exclusive to this shop. On next login any non-member is rejected.',
      };
      await this._loadBindingStatus();
    } finally {
      this._bindingBusy = false;
    }
  }

  private async _unbindThisShop() {
    if (!this.activeShop || !this._deviceId || this._bindingBusy) return;
    if (
      !confirm(
        'Unbind this device from this shop? Anyone signed in elsewhere will be able to use this tablet for any of their shops again.'
      )
    )
      return;
    this._bindingBusy = true;
    this._bindingMessage = null;
    try {
      const api = ApiFactory.authenticated();
      // Unbind by re-upserting with isExclusiveToShop=false. Keeps the
      // registration around (so reports / audit still find it) but
      // releases the cross-shop binding. Modes stay all-in-one for the same
      // reason as bind above (free tier permits only that; picker is Phase O).
      const r = await api.upsertDevice(
        this.activeShop.shopName,
        this._deviceId,
        {
          displayName: 'This device',
          allowedModes: ['all-in-one'],
          defaultMode: 'all-in-one',
          isExclusiveToShop: false,
        }
      );
      if (!r.success) {
        this._bindingMessage = {
          kind: 'danger',
          text: r.error ?? 'Could not unbind this device.',
        };
        return;
      }
      this._bindingMessage = {
        kind: 'success',
        text: 'This device is no longer exclusive to this shop.',
      };
      await this._loadBindingStatus();
    } finally {
      this._bindingBusy = false;
    }
  }

  private _onInput(field: 'displayName' | 'timezone') {
    return (e: Event) => {
      const value = (e.target as HTMLInputElement).value;
      if (field === 'displayName') this._displayName = value;
      if (field === 'timezone') this._timezone = value;
      this._saved = false;
    };
  }

  private _toggleVisibility(e: Event) {
    const checked = (e.target as HTMLInputElement).checked;
    this._visibility = checked ? 'public' : 'private';
    this._saved = false;
  }

  private _onWaitChange(field: 'warn' | 'danger') {
    return (e: Event) => {
      const raw = Number((e.target as HTMLInputElement).value);
      const value = Number.isFinite(raw) ? Math.max(1, Math.round(raw)) : 1;
      if (field === 'warn') this._waitWarn = value;
      else this._waitDanger = value;
      this._saved = false;
    };
  }

  private _onAnomalyChange(field: 'variance' | 'loss') {
    return (e: Event) => {
      const raw = Number((e.target as HTMLInputElement).value);
      if (!Number.isFinite(raw)) return;
      if (field === 'variance')
        this._variancePct = Math.max(0, Math.min(500, raw));
      else this._lossAmount = Math.max(0, raw);
      this._saved = false;
    };
  }

  private async _save(e: Event) {
    e.preventDefault();
    if (this._busy || !this._shop) return;
    this._busy = true;
    this._error = null;
    this._saved = false;

    try {
      const api = ApiFactory.authenticated();
      if (this._waitDanger < this._waitWarn) {
        this._error =
          'Danger threshold must be greater than or equal to the warn threshold.';
        return;
      }

      const r = await api.updateShopSettings(this._shop.shopName, {
        displayName: this._displayName.trim(),
        timezone: this._timezone.trim(),
        visibility: this._visibility,
        orderWaitWarnMinutes: this._waitWarn,
        orderWaitDangerMinutes: this._waitDanger,
        varianceThresholdPct: this._variancePct / 100,
        lossThresholdAmount: this._lossAmount,
      });
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Save failed.';
        return;
      }
      this._shop = r.data;
      this._waitWarn = r.data.orderWaitWarnMinutes ?? this._waitWarn;
      this._waitDanger = r.data.orderWaitDangerMinutes ?? this._waitDanger;
      this._variancePct = Math.round(
        (r.data.varianceThresholdPct ?? 0.2) * 100
      );
      this._lossAmount = r.data.lossThresholdAmount ?? this._lossAmount;
      this._saved = true;
    } catch (err) {
      this._error = (err as Error)?.message ?? 'Unexpected error.';
    } finally {
      this._busy = false;
    }
  }

  render() {
    if (this._loading) {
      return html`<sl-card><p>Loading…</p></sl-card>`;
    }
    if (this._error && !this._shop) {
      return html`<sl-alert variant="danger" open>${this._error}</sl-alert>`;
    }
    if (!this._shop) {
      return html`<sl-alert variant="warning" open>Shop not found.</sl-alert>`;
    }

    return html`
      <sl-card>
        <div slot="header">
          <h2 style="margin: 0;">${this._shop.displayName}</h2>
          <small>${this._shop.shopName}</small>
        </div>

        <form @submit=${this._save}>
          <sl-input
            label=${write`${'ref:shop:display-name'}Display name`}
            .value=${this._displayName}
            @sl-input=${this._onInput('displayName')}
            required
          ></sl-input>

          <sl-input
            label=${write`${'ref:shop:timezone'}Timezone`}
            help-text="IANA timezone, e.g. Australia/Sydney."
            .value=${this._timezone}
            @sl-input=${this._onInput('timezone')}
          ></sl-input>

          <sl-divider></sl-divider>

          <div class="visibility-row">
            <div class="vis-text">
              <strong
                >${this._visibility === 'public' ? 'Public' : 'Private'}</strong
              >
              <small>
                ${
                  this._visibility === 'public'
                    ? 'Customers can scan the QR code, see the menu, and place orders.'
                    : 'Only you and your staff can use this shop. Public surface is disabled.'
                }
              </small>
            </div>
            <sl-switch
              ?checked=${this._visibility === 'public'}
              @sl-change=${this._toggleVisibility}
            ></sl-switch>
          </div>

          <sl-divider></sl-divider>

          <div>
            <strong>Order wait time alerts</strong>
            <div style="opacity: 0.75; font-size: 0.85rem; margin: 4px 0 8px;">
              Cards on the active-orders board turn amber once an order has been
              waiting for the warn time, and red at the danger time.
            </div>
            <div
              style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;"
            >
              <sl-input
                type="number"
                min="1"
                label="Warn after (minutes)"
                .value=${String(this._waitWarn)}
                @sl-input=${this._onWaitChange('warn')}
              ></sl-input>
              <sl-input
                type="number"
                min="1"
                label="Danger after (minutes)"
                .value=${String(this._waitDanger)}
                @sl-input=${this._onWaitChange('danger')}
              ></sl-input>
            </div>
          </div>

          <sl-divider></sl-divider>

          <div>
            <strong>Stock-take anomaly thresholds</strong>
            <div style="opacity: 0.75; font-size: 0.85rem; margin: 4px 0 8px;">
              A counted line is flagged for review when its variance exceeds the
              percentage threshold OR when the negative-variance loss exceeds
              the value threshold. Higher thresholds mean fewer flags; tune for
              your shop's volume and value mix.
            </div>
            <div
              style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;"
            >
              <sl-input
                type="number"
                min="0"
                max="500"
                step="1"
                label="Variance threshold (%)"
                help-text="Default 20%."
                .value=${String(this._variancePct)}
                @sl-input=${this._onAnomalyChange('variance')}
              ></sl-input>
              <sl-input
                type="number"
                min="0"
                step="any"
                label=${`Loss threshold (${activeCurrency().symbol})`}
                help-text="Default 500."
                .value=${String(this._lossAmount)}
                @sl-input=${this._onAnomalyChange('loss')}
              ></sl-input>
            </div>
          </div>

          ${
            this._error
              ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>`
              : nothing
          }
          ${
            this._saved
              ? html`<sl-alert variant="success" open closable
                  >Settings saved.</sl-alert
                >`
              : nothing
          }

          <sl-button
            type="submit"
            variant="primary"
            ?loading=${this._busy}
            ?disabled=${this._busy}
          >
            Save settings
          </sl-button>
        </form>
      </sl-card>

      ${this._renderDeviceBindingSection()} ${this._renderResetSampleSection()}
    `;
  }

  // Owner self-serve reset for a sample shop. Wipes every sale, stock
  // movement, menu edit, supplier change etc. and reseeds the shop
  // from the same sample data the public onboarding uses. Confirm
  // dialog requires typing the shop name to avoid accidental wipes.
  private async _openResetDialog() {
    if (!this._shop?.isSample) return;
    this._resetDialogOpen = true;
    this._resetConfirmText = '';
    this._resetMessage = null;
  }

  private _cancelResetDialog() {
    if (this._resetBusy) return;
    this._resetDialogOpen = false;
    this._resetConfirmText = '';
  }

  private async _confirmResetSample() {
    if (!this._shop || !this._shop.isSample) return;
    if (
      this._resetConfirmText.trim().toLowerCase() !==
      this._shop.shopName.toLowerCase()
    ) {
      this._resetMessage = {
        kind: 'danger',
        text: 'The confirmation text did not match the shop name.',
      };
      return;
    }
    this._resetBusy = true;
    this._resetMessage = null;
    try {
      const r = await ApiFactory.authenticated().resetSampleShop(
        this._shop.shopName
      );
      if (!r.success || !r.data) {
        this._resetMessage = {
          kind: 'danger',
          text: r.error ?? 'Could not reset sample shop.',
        };
        return;
      }
      // The server renewed the token to reflect the freshly-seeded
      // shop. Update the user-profile so subscription gating + shop
      // list stay in sync without forcing a logout.
      if (this.userProfile && r.data.token) {
        const next = new UserProfile({
          ...this.userProfile.profile,
          accessToken: r.data.token,
        });
        window.dispatchEvent(
          new CustomEvent<UserProfile>('user-profile-set', { detail: next })
        );
      }
      // Adopt the freshly-read register and exit the dialog. The page
      // is still on /shop/settings; the user can navigate away when
      // ready.
      this._shop = r.data.shop;
      this._displayName = r.data.shop.displayName;
      this._resetDialogOpen = false;
      this._resetConfirmText = '';
      this._resetMessage = {
        kind: 'success',
        text: 'Sample shop reset. All data is back to the seed.',
      };
    } finally {
      this._resetBusy = false;
    }
  }

  private _renderResetSampleSection() {
    if (!this._shop?.isSample) return nothing;
    return html`
      <sl-card class="reset-sample" style="margin-top: 16px;">
        <div slot="header">
          <h3 style="margin: 0;">Reset sample shop</h3>
          <small>
            Wipe every sale, stock movement, menu change and custom data, and
            return this shop to the original sample seed. Useful when you want a
            clean slate to demo again.
          </small>
        </div>
        ${
          this._resetMessage
            ? html`
                <sl-alert
                  variant=${this._resetMessage.kind === 'success' ? 'success' : 'danger'}
                  open
                  closable
                  @sl-after-hide=${() => (this._resetMessage = null)}
                >
                  ${this._resetMessage.text}
                </sl-alert>
              `
            : nothing
        }
        <p style="font-size: 0.85rem; opacity: 0.8;">
          The shop name <code>${this._shop.shopName}</code> stays the same. Your
          login and team membership are preserved. Everything else inside the
          shop is replaced.
        </p>
        <sl-button
          variant="warning"
          @click=${this._openResetDialog}
          ?disabled=${this._resetBusy}
        >
          <sl-icon slot="prefix" name="arrow-clockwise"></sl-icon>
          Reset to sample seed
        </sl-button>
      </sl-card>

      <sl-dialog
        class="full-page-on-phone"
        label="Reset sample shop"
        ?open=${this._resetDialogOpen}
        @sl-request-close=${(e: Event) => {
          if (this._resetBusy) {
            e.preventDefault();
            return;
          }
          this._cancelResetDialog();
        }}
      >
        <p>
          This will <strong>permanently delete</strong> every sale, stock
          movement, menu edit, supplier change, purchase order and delivery on
          <code>${this._shop.shopName}</code>, and reseed the shop from the
          original sample data. There is no undo.
        </p>
        <p>Type <code>${this._shop.shopName}</code> below to confirm.</p>
        <sl-input
          placeholder="shop name"
          .value=${this._resetConfirmText}
          ?disabled=${this._resetBusy}
          @sl-input=${(e: Event) =>
            (this._resetConfirmText = (e.target as HTMLInputElement).value)}
        ></sl-input>
        ${
          this._resetMessage
            ? html`
                <sl-alert
                  variant=${this._resetMessage.kind === 'success' ? 'success' : 'danger'}
                  open
                >
                  ${this._resetMessage.text}
                </sl-alert>
              `
            : nothing
        }
        <sl-button
          slot="footer"
          variant="default"
          @click=${this._cancelResetDialog}
        >
          Cancel
        </sl-button>
        <sl-button
          slot="footer"
          variant="warning"
          ?loading=${this._resetBusy}
          ?disabled=${
            this._resetBusy ||
            this._resetConfirmText.trim().toLowerCase() !==
              this._shop.shopName.toLowerCase()
          }
          @click=${this._confirmResetSample}
        >
          Reset shop
        </sl-button>
      </sl-dialog>
    `;
  }

  private _renderDeviceBindingSection() {
    if (!this._shop) return nothing;
    // Three rendering branches based on the cross-shop binding state of
    // the current physical tablet:
    //   - bound to THIS shop      → show "this device is exclusive" + Unbind
    //   - bound to a DIFFERENT    → show "bound to other shop" + (no action;
    //     shop                       owner must unbind there first)
    //   - personal / unbound      → show "Bind this device to this shop"
    const boundToThisShop =
      this._bindingKind === 'shop-owned' &&
      this._bindingShopName?.toLowerCase() ===
        this._shop.shopName.toLowerCase();
    const boundElsewhere =
      this._bindingKind === 'shop-owned' && !boundToThisShop;

    return html`
      <sl-card class="device-binding">
        <div slot="header">
          <h3 style="margin: 0;">Device binding</h3>
          <small>
            Bind this physical tablet to this shop so any user logging in here
            is auto-routed to ${this._shop.displayName}. Non-members are
            rejected.
          </small>
        </div>

        ${
          this._deviceId
            ? html`
                <p style="font-size: 0.85rem; opacity: 0.75;">
                  Device id: <code>${this._deviceId}</code>
                </p>
              `
            : nothing
        }
        ${
          this._bindingKind === 'unknown'
            ? html`<sl-alert variant="neutral" open
                ><sl-icon slot="icon" name="hourglass-split"></sl-icon> Loading
                binding status…</sl-alert
              >`
            : nothing
        }
        ${
          boundToThisShop
            ? html`
                <sl-alert variant="success" open>
                  <sl-icon slot="icon" name="lock-fill"></sl-icon>
                  This device is exclusively bound to
                  <strong>${this._shop.displayName}</strong>. Only members of
                  this shop can sign in here.
                </sl-alert>
                <sl-button
                  variant="warning"
                  outline
                  ?loading=${this._bindingBusy}
                  ?disabled=${this._bindingBusy}
                  @click=${this._unbindThisShop}
                >
                  Unbind this device
                </sl-button>
              `
            : nothing
        }
        ${
          boundElsewhere
            ? html`
                <sl-alert variant="warning" open>
                  <sl-icon slot="icon" name="lock"></sl-icon>
                  This device is exclusively bound to another shop
                  (<code>${this._bindingShopName}</code>). Sign in there to
                  unbind it before re-binding here.
                </sl-alert>
              `
            : nothing
        }
        ${
          this._bindingKind === 'personal'
            ? html`
                <sl-alert variant="neutral" open>
                  <sl-icon slot="icon" name="unlock"></sl-icon>
                  This device is currently personal — anyone who can sign in
                  gets the standard shop picker.
                </sl-alert>
                <sl-button
                  variant="primary"
                  outline
                  ?loading=${this._bindingBusy}
                  ?disabled=${this._bindingBusy}
                  @click=${this._bindToThisShop}
                >
                  Bind this device to ${this._shop.displayName}
                </sl-button>
              `
            : nothing
        }
        ${
          this._bindingMessage
            ? html`
                <sl-alert
                  variant=${this._bindingMessage.kind === 'success' ? 'success' : 'danger'}
                  open
                  closable
                  @sl-after-hide=${() => (this._bindingMessage = null)}
                >
                  ${this._bindingMessage.text}
                </sl-alert>
              `
            : nothing
        }
      </sl-card>
    `;
  }
}
